import axios from 'axios'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000'

// Configuração global do axios para CORS
axios.defaults.withCredentials = false
axios.defaults.headers.common['Accept'] = 'application/json'

export interface DiscordIntegration {
  id: number
  instance_id: number
  user_id: number
  discord_user_id: string
  discord_username: string
  discord_discriminator: string | null
  guild_id: string | null
  guild_name: string | null
  is_active: boolean
  last_sync_at: string | null
  sync_frequency: 'realtime' | 'daily' | 'weekly' | 'manual'
  mapped_channels_count?: number
  settings: any
  created_at: string
  updated_at: string
}

export interface DiscordIntegrationStatus {
  is_connected: boolean
  integration: DiscordIntegration | null
}

export interface DiscordGuild {
  id: string
  name: string
  icon: string | null
  member_count?: number
}

export interface DiscordChannel {
  id: string
  name: string
  type: 'text' | 'voice' | 'category' | number // Aceita número também (0 = text, 2 = voice, 4 = category)
  position?: number
}

export interface DiscordChannelMapping {
  id: number
  discord_integration_id: number
  guild_id: string
  channel_id: string
  channel_name: string
  project_id: number
  project_name?: string
  task_creation_mode: 'manual' | 'suggest'
  is_active: boolean
  created_at: string
  updated_at: string
}

class DiscordApiService {
  private getAuthHeaders() {
    // Usa a mesma chave de autenticação do sistema
    const authKey = 'impersonating-auth-token';
    const authData = localStorage.getItem(authKey);
    
    let token = null;
    if (authData) {
      try {
        const auth = JSON.parse(authData);
        token = auth.api_token;
      } catch (error) {
        console.error('Erro ao parsear dados de autenticação:', error);
      }
    }
    
    // Fallback para outras chaves
    if (!token) {
      token = localStorage.getItem('access_token') || 
              localStorage.getItem('token') || 
              localStorage.getItem('auth_token');
    }
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      console.warn('Nenhum token de autenticação encontrado para Discord API');
    }
    
    return headers;
  }

  /**
   * Obtém a URL de autorização OAuth do Discord
   */
  async getAuthorizationUrl(returnUrl?: string): Promise<string> {
    try {
      const params: any = {};
      if (returnUrl) {
        params.return_url = returnUrl;
      }

      const response = await axios.get(`${API_BASE_URL}/api/integrations/discord/authorization-url`, {
        headers: this.getAuthHeaders(),
        params
      })

      if (response.data.success) {
        return response.data.data.authorization_url
      }

      throw new Error(response.data.message || 'Erro ao obter URL de autorização')
    } catch (error: any) {
      console.error('Error getting authorization URL:', error)
      throw error
    }
  }

  /**
   * Conecta integração usando código OAuth (chamado após callback)
   * Nota: O callback do Discord já processa a conexão, este método é para casos especiais
   */
  async connect(code?: string, guildId?: string): Promise<DiscordIntegration> {
    try {
      const payload: any = {};
      if (code) {
        payload.code = code;
      }
      if (guildId) {
        payload.guild_id = guildId;
      }

      const response = await axios.post(`${API_BASE_URL}/api/integrations/discord/connect`, payload, {
        headers: this.getAuthHeaders()
      })

      if (response.data.success) {
        return response.data.data.integration
      }

      throw new Error(response.data.message || 'Erro ao conectar integração')
    } catch (error: any) {
      console.error('Error connecting Discord:', error)
      throw error
    }
  }

  /**
   * Retorna status da integração
   */
  async getStatus(): Promise<DiscordIntegrationStatus> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/integrations/discord/status`, {
        headers: this.getAuthHeaders()
      })

      if (response.data.success) {
        return response.data.data
      }

      throw new Error(response.data.message || 'Erro ao obter status da integração')
    } catch (error: any) {
      console.error('Error getting Discord integration status:', error)
      // Retorna status desconectado em caso de erro
      return {
        is_connected: false,
        integration: null
      }
    }
  }

  /**
   * Desconecta integração
   */
  async disconnect(): Promise<void> {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/integrations/discord/disconnect`, {}, {
        headers: this.getAuthHeaders()
      })

      if (!response.data.success) {
        throw new Error(response.data.message || 'Erro ao desconectar')
      }
    } catch (error: any) {
      console.error('Error disconnecting Discord:', error)
      throw error
    }
  }

  /**
   * Lista servidores (guilds) do Discord
   */
  async getGuilds(): Promise<DiscordGuild[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/integrations/discord/guilds`, {
        headers: this.getAuthHeaders()
      })

      if (response.data.success) {
        return response.data.data.guilds || []
      }

      throw new Error(response.data.message || 'Erro ao obter servidores')
    } catch (error: any) {
      console.error('Error getting guilds:', error)
      throw error
    }
  }

  /**
   * Lista canais de um servidor
   */
  async getChannels(guildId: string): Promise<DiscordChannel[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/integrations/discord/guilds/${guildId}/channels`, {
        headers: this.getAuthHeaders()
      })

      if (response.data.success) {
        const channels = response.data.data.channels || []
        // Garante que seja um array
        if (!Array.isArray(channels)) {
          console.warn('Channels response is not an array:', channels)
          return []
        }
        return channels
      }

      throw new Error(response.data.message || 'Erro ao obter canais')
    } catch (error: any) {
      console.error('Error getting channels:', error)
      throw error
    }
  }

  /**
   * Cria mapeamento de canal para projeto
   */
  async createChannelMapping(data: {
    guild_id: string
    channel_id: string
    channel_name?: string
    project_id: number
    task_creation_mode: 'manual' | 'suggest'
  }): Promise<DiscordChannelMapping> {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/integrations/discord/channel-mappings`, data, {
        headers: this.getAuthHeaders()
      })

      if (response.data.success) {
        return response.data.data.mapping
      }

      throw new Error(response.data.message || 'Erro ao criar mapeamento')
    } catch (error: any) {
      console.error('Error creating channel mapping:', error)
      throw error
    }
  }

  /**
   * Lista mapeamentos de canais
   */
  async getChannelMappings(): Promise<DiscordChannelMapping[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/integrations/discord/channel-mappings`, {
        headers: this.getAuthHeaders()
      })

      if (response.data.success) {
        return response.data.data.mappings || []
      }

      throw new Error(response.data.message || 'Erro ao obter mapeamentos')
    } catch (error: any) {
      console.error('Error getting channel mappings:', error)
      return []
    }
  }

  /**
   * Atualiza mapeamento de canal
   */
  async updateChannelMapping(id: number, data: {
    project_id: number
    task_creation_mode: 'manual' | 'suggest'
    channel_name?: string
  }): Promise<DiscordChannelMapping> {
    try {
      const response = await axios.put(`${API_BASE_URL}/api/integrations/discord/channel-mappings/${id}`, data, {
        headers: this.getAuthHeaders()
      })

      if (response.data.success) {
        return response.data.data.mapping
      }

      throw new Error(response.data.message || 'Erro ao atualizar mapeamento')
    } catch (error: any) {
      console.error('Error updating channel mapping:', error)
      throw error
    }
  }

  /**
   * Remove mapeamento de canal
   */
  async deleteChannelMapping(id: number): Promise<void> {
    try {
      const response = await axios.delete(`${API_BASE_URL}/api/integrations/discord/channel-mappings/${id}`, {
        headers: this.getAuthHeaders()
      })

      if (!response.data.success) {
        throw new Error(response.data.message || 'Erro ao remover mapeamento')
      }
    } catch (error: any) {
      console.error('Error deleting channel mapping:', error)
      throw error
    }
  }

  /**
   * Atualiza configurações da integração
   */
  async updateSettings(settings: any, guildId?: string, guildName?: string): Promise<DiscordIntegration> {
    try {
      const payload: any = { settings }
      
      if (guildId) {
        payload.guild_id = guildId
      }
      if (guildName) {
        payload.guild_name = guildName
      }

      const response = await axios.put(`${API_BASE_URL}/api/integrations/discord/settings`, payload, {
        headers: this.getAuthHeaders()
      })

      if (response.data.success) {
        return response.data.data.integration
      }

      throw new Error(response.data.message || 'Erro ao atualizar configurações')
    } catch (error: any) {
      console.error('Error updating settings:', error)
      throw error
    }
  }

}

export const discordApiService = new DiscordApiService()

